/**
 * Money is always an integer number of grosze (1/100 PLN). Floats are used only
 * for display formatting, never for storage or arithmetic.
 */

/** Upper bound for a single amount: 1 000 000 zł (fits easily in a signed INT). */
export const MAX_AMOUNT_GROSZE = 100_000_000;

const plnFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `12345` → `"123,45 zł"` (with Polish grouping and non-breaking spaces). */
export function formatMoney(grosze: number): string {
  if (!Number.isInteger(grosze)) throw new TypeError('grosze must be an integer');
  return plnFormatter.format(grosze / 100);
}

/** `12345` → `"123,45"`; used to pre-fill form inputs. */
export function groszeToInput(grosze: number | null | undefined): string {
  if (grosze == null) return '';
  const sign = grosze < 0 ? '-' : '';
  const abs = Math.abs(grosze);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  return frac === 0 ? `${sign}${whole}` : `${sign}${whole},${String(frac).padStart(2, '0')}`;
}

/**
 * Parses user input such as `"150"`, `"150,5"`, `"1 234,50 zł"` or `"1.234,50"`
 * into grosze. Returns `null` for empty or invalid input. Negative amounts are rejected.
 */
export function parseMoney(input: string): number | null {
  const s = input.replace(/zł|pln/gi, '').replace(/[\s\u00a0\u202f]/g, '');
  if (s === '') return null;

  let whole: string;
  let frac: string;
  // Plain number with an optional comma or dot decimal part: "1234", "1234,5", "1234.50".
  const plain = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(s);
  // Dots as thousands separators with an optional comma decimal part: "1.234", "1.234,50".
  const grouped = /^(\d{1,3}(?:\.\d{3})+)(?:,(\d{1,2}))?$/.exec(s);
  if (grouped) {
    whole = grouped[1]!.replace(/\./g, '');
    frac = grouped[2] ?? '';
  } else if (plain) {
    whole = plain[1]!;
    frac = plain[2] ?? '';
  } else {
    return null;
  }

  const grosze = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(grosze) || grosze > MAX_AMOUNT_GROSZE) return null;
  return grosze;
}

export function sumGrosze(values: Array<number | null | undefined>): number {
  return values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
}
